import { useLocalStorage, useMediaQuery } from '@passkeys/hooks';
import type { ReactNode } from 'react';
import { createContext, useCallback, useEffect, useMemo } from 'react';

import { setDocumentDataAttribute } from '@/ui/utils/document';
import { EMBEDDABLE_SIGNER } from '../constants/index.js';
import exodus from '../exodus.js';

const COLOR_SCHEME_QUERY = '(prefers-color-scheme: dark)';

const THEME_DATASET_KEY = 'theme';
const THEME_LOCALSTORAGE_KEY = 'ui!theme';

export type Theme = 'system' | 'dark' | 'light';

export interface StandaloneConfig {
  theme: Theme;
}

export type StandaloneConfigContextValue = StandaloneConfig & {
  setTheme: (theme: Theme) => void;
};

const defaultValue: StandaloneConfigContextValue = {
  theme: 'system',
  setTheme: () => {},
};

const StandaloneConfigContext =
  createContext<StandaloneConfigContextValue>(defaultValue);

export interface StandaloneConfigProviderProps {
  children: ReactNode;
}

export function StandaloneConfigProvider({
  children,
}: StandaloneConfigProviderProps) {
  if (!__STANDALONE_MODE__) {
    throw new Error(
      'StandaloneConfigProvider should only be used in standalone mode',
    );
  }

  const [theme, setStoredTheme] = useLocalStorage<Theme>(
    THEME_LOCALSTORAGE_KEY,
    'system',
  );

  const setTheme = useCallback(
    (theme: Theme) => {
      setStoredTheme(theme);

      if (EMBEDDABLE_SIGNER) {
        exodus.passkey.setTheme({ theme });
      }
    },
    [setStoredTheme],
  );

  const isSystemDarkTheme = useMediaQuery(COLOR_SCHEME_QUERY, {
    initializeWithValue: true,
  });

  const isDarkTheme =
    (theme === 'system' && isSystemDarkTheme) || theme === 'dark';

  useEffect(() => {
    setDocumentDataAttribute(
      THEME_DATASET_KEY,
      isDarkTheme ? 'dark' : undefined,
    );
  }, [isDarkTheme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme,
    }),
    [setTheme, theme],
  );

  return (
    <StandaloneConfigContext.Provider value={value}>
      {children}
    </StandaloneConfigContext.Provider>
  );
}

export default StandaloneConfigContext;
